/**
 * useShine Hook
 * Provides shine effect functionality for UI components.
 * Always disabled when prefers-reduced-motion: reduce (a11y).
 */

import React, { useEffect, useState } from 'react';

export interface ShineConfig {
  enabled?: boolean;
  borderWidth?: number;
  duration?: number;
  intensity?: number;
  paused?: boolean;
  hoverBoost?: boolean;
  hideBorders?: boolean;
  customColors?: {
    primary?: string;
    secondary?: string;
  };
  themeMode?: 'light' | 'dark';
}

type ShineElementType = 'default' | 'sidebar' | 'header' | 'content' | 'footer';

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  return reduced;
}

/**
 * Hook to create shine effect elements
 * @param type - Type of shine element
 * @param config - Shine configuration
 * @returns React element or null
 */
export function useShine(
  type: ShineElementType = 'default',
  config: ShineConfig = {}
): React.ReactElement | null {
  const { enabled = false } = config;
  const prefersReducedMotion = usePrefersReducedMotion();

  if (!enabled || prefersReducedMotion) {
    return null;
  }

  // Decorative shine overlay — never block pointer events; data-shine for global PRM CSS
  return React.createElement('div', {
    'data-shine': type,
    className: 'shine-effect',
    'aria-hidden': true,
    style: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      zIndex: 1,
      opacity: config.intensity || 0.3,
    },
  });
}

